import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { RequestStatusObject, buildReducers, createRequestStatusObject } from '../utils';
import { EasterEgg, CuratedQuote, EngagementQuote, EngagementTip, MoodEntry, MoodGif, MoodState, QuoteSettings } from '../../types/engagement';

interface EngagementSliceState {
	/** The most recently fetched quote of the moment. */
	quote: EngagementQuote | null;
	/** Active homepage tips for the current user (minus dismissed). */
	tips: EngagementTip[];
	/** Admin-managed curated fallback quotes. */
	quotes: CuratedQuote[];
	/** Admin-controlled quote rotation settings (dynamic pool on/off). */
	quoteSettings: QuoteSettings | null;
	/** Full tip set for admin management (includes retired). */
	adminTips: EngagementTip[];
	/** The current user's mood for today, or null if not set. */
	mood: string | null;
	/** The current user's recent mood history. */
	moodHistory: MoodEntry[];
	/** Admin-managed mood reaction GIFs. */
	moodGifs: MoodGif[];
	/** Admin-configured homepage easter eggs. */
	easterEggs: EasterEgg[];

	quoteRequest: RequestStatusObject;
	tipsRequest: RequestStatusObject;
	dismissTipRequest: RequestStatusObject;
	quotesRequest: RequestStatusObject;
	saveQuoteRequest: RequestStatusObject;
	retireQuoteRequest: RequestStatusObject;
	quoteSettingsRequest: RequestStatusObject;
	updateQuoteSettingsRequest: RequestStatusObject;
	adminTipsRequest: RequestStatusObject;
	saveTipRequest: RequestStatusObject;
	retireTipRequest: RequestStatusObject;
	moodRequest: RequestStatusObject;
	setMoodRequest: RequestStatusObject;
	moodGifsRequest: RequestStatusObject;
	saveMoodGifRequest: RequestStatusObject;
	retireMoodGifRequest: RequestStatusObject;
	easterEggsRequest: RequestStatusObject;
	saveEasterEggRequest: RequestStatusObject;
	retireEasterEggRequest: RequestStatusObject;
}

const initialState: EngagementSliceState = {
	quote: null,
	tips: [],
	quotes: [],
	quoteSettings: null,
	adminTips: [],
	mood: null,
	moodHistory: [],
	moodGifs: [],
	easterEggs: [],

	quoteRequest: createRequestStatusObject(),
	tipsRequest: createRequestStatusObject(),
	dismissTipRequest: createRequestStatusObject(),
	quotesRequest: createRequestStatusObject(),
	saveQuoteRequest: createRequestStatusObject(),
	retireQuoteRequest: createRequestStatusObject(),
	quoteSettingsRequest: createRequestStatusObject(),
	updateQuoteSettingsRequest: createRequestStatusObject(),
	adminTipsRequest: createRequestStatusObject(),
	saveTipRequest: createRequestStatusObject(),
	retireTipRequest: createRequestStatusObject(),
	moodRequest: createRequestStatusObject(),
	setMoodRequest: createRequestStatusObject(),
	moodGifsRequest: createRequestStatusObject(),
	saveMoodGifRequest: createRequestStatusObject(),
	retireMoodGifRequest: createRequestStatusObject(),
	easterEggsRequest: createRequestStatusObject(),
	saveEasterEggRequest: createRequestStatusObject(),
	retireEasterEggRequest: createRequestStatusObject()
};

/** Fetch a quote of the moment (external provider with curated/bundled fallback). */
export const getQuote = createAsyncThunk<
	EngagementQuote,
	{ mood?: string } | undefined,
	{ dispatch: AppDispatch; state: RootState }
>('engagement/getQuote', async (arg) => {
	// Cache-bust so the browser/API Gateway never serves a stale 304 — each
	// click should surface a genuinely fresh quote. When a mood is supplied the
	// backend serves a mood-appropriate quote from its tagged pools.
	const queryStringParameters: Record<string, string | number> = { t: Date.now() };
	if (arg?.mood) {
		queryStringParameters.mood = arg.mood;
	}
	const response = await API.get('inspirusapi', '/engagement/quote', {
		headers: { 'Cache-Control': 'no-cache' },
		queryStringParameters
	});
	return response as EngagementQuote;
});

/** Fetch the active homepage tips for the current user. */
export const listTips = createAsyncThunk<
	EngagementTip[],
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('engagement/listTips', async () => {
	const response = await API.get('inspirusapi', '/engagement/tips', { headers: {} });
	return response.items ?? [];
});

/** Dismiss a tip for the current user so it stops appearing. */
export const dismissTip = createAsyncThunk<
	string,
	{ id: string },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/dismissTip', async ({ id }) => {
	await API.post('inspirusapi', `/engagement/tips/${id}/dismiss`, { headers: {} });
	return id;
});

/** List curated fallback quotes (admin). Includes retired ones for management. */
export const listQuotes = createAsyncThunk<
	CuratedQuote[],
	{ includeInactive?: boolean } | undefined,
	{ dispatch: AppDispatch; state: RootState }
>('engagement/listQuotes', async (arg) => {
	const path = arg?.includeInactive
		? '/engagement/quotes?includeInactive=true'
		: '/engagement/quotes';
	const response = await API.get('inspirusapi', path, { headers: {} });
	return response.items ?? [];
});

/** Create or update a curated fallback quote (admin). */
export const saveQuote = createAsyncThunk<
	CuratedQuote,
	{ quote: Partial<CuratedQuote> & { id?: string } },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/saveQuote', async ({ quote }) => {
	if (quote.id) {
		const response = await API.put('inspirusapi', `/engagement/quotes/${quote.id}`, {
			headers: {},
			body: quote
		});
		return response as CuratedQuote;
	}
	const response = await API.post('inspirusapi', '/engagement/quotes', {
		headers: {},
		body: quote
	});
	return response as CuratedQuote;
});

/** Retire (soft-delete) a curated fallback quote (admin). */
export const retireQuote = createAsyncThunk<
	string,
	{ id: string },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/retireQuote', async ({ id }) => {
	await API.del('inspirusapi', `/engagement/quotes/${id}`, { headers: {} });
	return id;
});

/** Fetch the quote-rotation settings (admin). */
export const getQuoteSettings = createAsyncThunk<
	QuoteSettings,
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('engagement/getQuoteSettings', async () => {
	const response = await API.get('inspirusapi', '/engagement/quotes/settings', { headers: {} });
	return response as QuoteSettings;
});

/** Update the quote-rotation settings — toggle the dynamic pool on/off (admin). */
export const updateQuoteSettings = createAsyncThunk<
	QuoteSettings,
	{ dynamicQuotesEnabled: boolean },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/updateQuoteSettings', async ({ dynamicQuotesEnabled }) => {
	const response = await API.put('inspirusapi', '/engagement/quotes/settings', {
		headers: {},
		body: { dynamicQuotesEnabled }
	});
	return response as QuoteSettings;
});

/** List all homepage tips (admin), including retired ones, for management. */
export const listAdminTips = createAsyncThunk<
	EngagementTip[],
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('engagement/listAdminTips', async () => {
	const response = await API.get('inspirusapi', '/engagement/tips?includeInactive=true', {
		headers: {}
	});
	return response.items ?? [];
});

/** Create or update a homepage tip (admin). */
export const saveTip = createAsyncThunk<
	EngagementTip,
	{ tip: Partial<EngagementTip> & { id?: string } },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/saveTip', async ({ tip }) => {
	if (tip.id) {
		const response = await API.put('inspirusapi', `/engagement/tips/${tip.id}`, {
			headers: {},
			body: tip
		});
		return response as EngagementTip;
	}
	const response = await API.post('inspirusapi', '/engagement/tips', {
		headers: {},
		body: tip
	});
	return response as EngagementTip;
});

/** Retire (soft-delete) a homepage tip (admin). */
export const retireTip = createAsyncThunk<
	string,
	{ id: string },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/retireTip', async ({ id }) => {
	await API.del('inspirusapi', `/engagement/tips/${id}`, { headers: {} });
	return id;
});

/** Fetch the current user's mood for today and recent history. */
export const getMood = createAsyncThunk<
	MoodState,
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>('engagement/getMood', async () => {
	const response = await API.get('inspirusapi', '/engagement/mood', { headers: {} });
	return {
		date: response.date,
		mood: response.mood ?? null,
		history: response.history ?? []
	};
});

/** Set the current user's mood for today. */
export const setMood = createAsyncThunk<
	MoodEntry,
	{ mood: string },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/setMood', async ({ mood }) => {
	const response = await API.put('inspirusapi', '/engagement/mood', {
		headers: {},
		body: { mood }
	});
	return response as MoodEntry;
});

/** Fetch mood reaction GIFs. Admins may include retired ones. */
export const listMoodGifs = createAsyncThunk<
	MoodGif[],
	{ includeInactive?: boolean } | undefined,
	{ dispatch: AppDispatch; state: RootState }
>('engagement/listMoodGifs', async (arg) => {
	const path = arg?.includeInactive
		? '/engagement/mood-gifs?includeInactive=true'
		: '/engagement/mood-gifs';
	const response = await API.get('inspirusapi', path, { headers: {} });
	return response.items ?? [];
});

/** Create or update a mood reaction GIF (admin). */
export const saveMoodGif = createAsyncThunk<
	MoodGif,
	{ gif: Partial<MoodGif> & { id?: string } },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/saveMoodGif', async ({ gif }) => {
	if (gif.id) {
		const response = await API.put('inspirusapi', `/engagement/mood-gifs/${gif.id}`, {
			headers: {},
			body: gif
		});
		return response as MoodGif;
	}
	const response = await API.post('inspirusapi', '/engagement/mood-gifs', {
		headers: {},
		body: gif
	});
	return response as MoodGif;
});

/** Retire (soft-delete) a mood reaction GIF (admin). */
export const retireMoodGif = createAsyncThunk<
	string,
	{ id: string },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/retireMoodGif', async ({ id }) => {
	await API.del('inspirusapi', `/engagement/mood-gifs/${id}`, { headers: {} });
	return id;
});

/** Fetch homepage easter eggs. Admins may include retired ones. */
export const listEasterEggs = createAsyncThunk<
	EasterEgg[],
	{ includeInactive?: boolean } | undefined,
	{ dispatch: AppDispatch; state: RootState }
>('engagement/listEasterEggs', async (arg) => {
	const path = arg?.includeInactive
		? '/engagement/easter-eggs?includeInactive=true'
		: '/engagement/easter-eggs';
	const response = await API.get('inspirusapi', path, { headers: {} });
	return response.items ?? [];
});

/** Create or update a homepage easter egg (admin). */
export const saveEasterEgg = createAsyncThunk<
	EasterEgg,
	{ egg: Partial<EasterEgg> & { id?: string } },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/saveEasterEgg', async ({ egg }) => {
	if (egg.id) {
		const response = await API.put('inspirusapi', `/engagement/easter-eggs/${egg.id}`, {
			headers: {},
			body: egg
		});
		return response as EasterEgg;
	}
	const response = await API.post('inspirusapi', '/engagement/easter-eggs', {
		headers: {},
		body: egg
	});
	return response as EasterEgg;
});

/** Retire (soft-delete) a homepage easter egg (admin). */
export const retireEasterEgg = createAsyncThunk<
	string,
	{ id: string },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/retireEasterEgg', async ({ id }) => {
	await API.del('inspirusapi', `/engagement/easter-eggs/${id}`, { headers: {} });
	return id;
});

/**
 * Record that the current user solved an easter egg. This persists a completion
 * marker server-side, which the badge evaluator reacts to by unlocking any badge
 * whose criteria target this egg. Fire-and-forget: it holds no slice state.
 */
export const recordEasterEggFound = createAsyncThunk<
	{ easterEggId: string; recorded: boolean },
	{ id: string },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/recordEasterEggFound', async ({ id }) => {
	const response = await API.post('inspirusapi', `/engagement/easter-eggs/${id}/found`, {
		headers: {},
		body: {}
	});
	return response as { easterEggId: string; recorded: boolean };
});

/**
 * Report the signed-in user's result for a mini-game stat (e.g. best streak,
 * fewest moves). The backend keeps only genuine personal bests, and the badge
 * evaluator unlocks any game badge whose threshold the new best meets.
 * Fire-and-forget: it holds no slice state.
 */
export const reportGameStat = createAsyncThunk<
	{ gameId: string; stat: string; value: number; best?: number; improved: boolean },
	{ gameId: string; stat: string; value: number },
	{ dispatch: AppDispatch; state: RootState }
>('engagement/reportGameStat', async ({ gameId, stat, value }) => {
	const response = await API.post('inspirusapi', `/engagement/games/${gameId}/stats`, {
		headers: {},
		body: { stat, value }
	});
	return response as { gameId: string; stat: string; value: number; best?: number; improved: boolean };
});

const engagementSlice = createSlice({
	name: 'engagement',
	initialState,
	reducers: {
		resetEngagement: () => initialState
	},
	extraReducers: (builder) => {
		buildReducers(builder, getQuote, 'quoteRequest', {
			onFulfilled: (state, payload) => {
				state.quote = payload;
			}
		});
		buildReducers(builder, listTips, 'tipsRequest', {
			onFulfilled: (state, payload) => {
				state.tips = payload;
			}
		});
		buildReducers(builder, dismissTip, 'dismissTipRequest', {
			onFulfilled: (state, payload) => {
				state.tips = state.tips.filter((tip: EngagementTip) => tip.id !== payload);
			}
		});
		buildReducers(builder, listQuotes, 'quotesRequest', {
			onFulfilled: (state, payload) => {
				state.quotes = payload;
			}
		});
		buildReducers(builder, saveQuote, 'saveQuoteRequest', {
			onFulfilled: (state, payload) => {
				const index = state.quotes.findIndex((quote: CuratedQuote) => quote.id === payload.id);
				if (index >= 0) {
					state.quotes[index] = payload;
				} else {
					state.quotes.push(payload);
				}
			}
		});
		buildReducers(builder, retireQuote, 'retireQuoteRequest', {
			onFulfilled: (state, payload) => {
				const index = state.quotes.findIndex((quote: CuratedQuote) => quote.id === payload);
				if (index >= 0) {
					state.quotes[index] = { ...state.quotes[index], active: false };
				}
			}
		});
		buildReducers(builder, getQuoteSettings, 'quoteSettingsRequest', {
			onFulfilled: (state, payload) => {
				state.quoteSettings = payload;
			}
		});
		buildReducers(builder, updateQuoteSettings, 'updateQuoteSettingsRequest', {
			onFulfilled: (state, payload) => {
				state.quoteSettings = payload;
			}
		});
		buildReducers(builder, listAdminTips, 'adminTipsRequest', {
			onFulfilled: (state, payload) => {
				state.adminTips = payload;
			}
		});
		buildReducers(builder, saveTip, 'saveTipRequest', {
			onFulfilled: (state, payload) => {
				const index = state.adminTips.findIndex((tip: EngagementTip) => tip.id === payload.id);
				if (index >= 0) {
					state.adminTips[index] = payload;
				} else {
					state.adminTips.push(payload);
				}
			}
		});
		buildReducers(builder, retireTip, 'retireTipRequest', {
			onFulfilled: (state, payload) => {
				const index = state.adminTips.findIndex((tip: EngagementTip) => tip.id === payload);
				if (index >= 0) {
					state.adminTips[index] = { ...state.adminTips[index], active: false };
				}
			}
		});
		buildReducers(builder, getMood, 'moodRequest', {
			onFulfilled: (state, payload) => {
				state.mood = payload.mood;
				state.moodHistory = payload.history;
			}
		});
		buildReducers(builder, setMood, 'setMoodRequest', {
			onFulfilled: (state, payload) => {
				state.mood = payload.mood;
				const index = state.moodHistory.findIndex(
					(entry: MoodEntry) => entry.date === payload.date
				);
				if (index >= 0) {
					state.moodHistory[index] = payload;
				} else {
					state.moodHistory.unshift(payload);
				}
			}
		});
		buildReducers(builder, listMoodGifs, 'moodGifsRequest', {
			onFulfilled: (state, payload) => {
				state.moodGifs = payload;
			}
		});
		buildReducers(builder, saveMoodGif, 'saveMoodGifRequest', {
			onFulfilled: (state, payload) => {
				const index = state.moodGifs.findIndex((gif: MoodGif) => gif.id === payload.id);
				if (index >= 0) {
					state.moodGifs[index] = payload;
				} else {
					state.moodGifs.push(payload);
				}
			}
		});
		buildReducers(builder, retireMoodGif, 'retireMoodGifRequest', {
			onFulfilled: (state, payload) => {
				const index = state.moodGifs.findIndex((gif: MoodGif) => gif.id === payload);
				if (index >= 0) {
					state.moodGifs[index] = { ...state.moodGifs[index], active: false };
				}
			}
		});
		buildReducers(builder, listEasterEggs, 'easterEggsRequest', {
			onFulfilled: (state, payload) => {
				state.easterEggs = payload;
			}
		});
		buildReducers(builder, saveEasterEgg, 'saveEasterEggRequest', {
			onFulfilled: (state, payload) => {
				const index = state.easterEggs.findIndex((egg: EasterEgg) => egg.id === payload.id);
				if (index >= 0) {
					state.easterEggs[index] = payload;
				} else {
					state.easterEggs.push(payload);
				}
			}
		});
		buildReducers(builder, retireEasterEgg, 'retireEasterEggRequest', {
			onFulfilled: (state, payload) => {
				const index = state.easterEggs.findIndex((egg: EasterEgg) => egg.id === payload);
				if (index >= 0) {
					state.easterEggs[index] = { ...state.easterEggs[index], active: false };
				}
			}
		});
	}
});

export const { resetEngagement } = engagementSlice.actions;
export default engagementSlice.reducer;
